import { useMutation, useQuery } from '@apollo/client';
import type { FlagQueryFilterForGrid } from '@generated/graphql';
import {
  type BulkTransitionFlagsForGridMutation,
  type FetchFlagsForGridQuery,
  FlagStates,
  FlagTransition,
} from '@generated/graphql';
import type {
  DataTableTanstackTable,
  PaginationState,
  RowSelectionState,
  RowsPerPageOption,
} from '@ovComponents/2-component/dataTable';
import { useDataTable } from '@ovComponents/2-component/dataTable';
import type { OnChangeFn } from '@tanstack/react-table';
import type { Theme } from 'hephaestus/core';
import { useSnackbar } from 'hephaestus/web';
import type { TFunction } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildReconFlagColumns } from '../table/reconFlagsGrid.columns';
import {
  BULK_TRANSITION_FLAGS_FOR_GRID,
  FETCH_FLAGS_FOR_GRID,
} from './flags.queries';
import type { ReconFlagRow } from './reconFlags.types';
import {
  buildFetchFlagsGridVariables,
  shouldSuppressReconFlagsGridResults,
} from './reconFlagsGridVariables';

type UseReconFlagsGridInput = {
  theme: Theme;
  t: TFunction;
  orgId: string | undefined;
  gridFilter: FlagQueryFilterForGrid | null;
  flagState: string;
  page: number;
  setPage: (page: number) => void;
  pageSize: RowsPerPageOption;
  setPageSize: (size: RowsPerPageOption) => void;
  onRowClick: (row: ReconFlagRow) => void;
  refetchAfterFlagMutation: () => Promise<unknown>;
};

export const useReconFlagsGrid = ({
  theme,
  t,
  orgId,
  gridFilter,
  flagState,
  page,
  setPage,
  pageSize,
  setPageSize,
  onRowClick,
  refetchAfterFlagMutation,
}: UseReconFlagsGridInput): {
  gridProps: {
    theme: Theme;
    table: DataTableTanstackTable<ReconFlagRow>;
    pageSize: RowsPerPageOption;
    tableLoading: boolean;
    pageCount: number;
    page: number;
    onPageChange: (p: number) => void;
    totalRowCount: number;
    onPerPageChange: (perPage: number) => void;
    onRowClick: (row: ReconFlagRow) => void;
    selectedCount: number;
    onSelectAllOnPage: () => void;
    onClearSelection: () => void;
    onBulkDismiss: () => Promise<boolean>;
    bulkDismissLoading: boolean;
    gridQueryLoading: boolean;
  };
  refetch: () => Promise<unknown>;
} => {
  const { enqueueSnackbar } = useSnackbar();

  const [resolvedGridFilterKey, setResolvedGridFilterKey] = useState<
    string | null
  >(null);

  const gridFilterKey = useMemo(() => JSON.stringify(gridFilter), [gridFilter]);

  const gridQueryVariables = useMemo(
    () =>
      buildFetchFlagsGridVariables({
        gridFilter,
        pageSize,
        page,
        flagState,
      }),
    [gridFilter, pageSize, page, flagState],
  );

  const { loading, data, refetch, previousData } =
    useQuery<FetchFlagsForGridQuery>(FETCH_FLAGS_FOR_GRID, {
      variables: gridQueryVariables,
      skip: !orgId,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
    });

  useEffect(() => {
    if (!loading && data) {
      setResolvedGridFilterKey(gridFilterKey);
    }
  }, [loading, data, gridFilterKey]);

  const suppressGridResults = shouldSuppressReconFlagsGridResults(
    loading,
    gridFilterKey,
    resolvedGridFilterKey,
  );

  const serverFlags: ReconFlagRow[] = useMemo(() => {
    if (suppressGridResults) {
      return [];
    }
    const gridResult =
      loading && gridFilterKey !== resolvedGridFilterKey
        ? data
        : (data ?? previousData);
    return gridResult?.fetchFlagsForGrid?.flags ?? [];
  }, [
    suppressGridResults,
    data,
    previousData,
    loading,
    gridFilterKey,
    resolvedGridFilterKey,
  ]);

  const filteredGridTotalCount = useMemo(() => {
    if (suppressGridResults) {
      return 0;
    }
    const gridResult =
      loading && gridFilterKey !== resolvedGridFilterKey
        ? data
        : (data ?? previousData);
    return gridResult?.fetchFlagsForGrid?.totalCount ?? 0;
  }, [
    suppressGridResults,
    loading,
    gridFilterKey,
    resolvedGridFilterKey,
    data,
    previousData,
  ]);

  const bulkSelectionEnabled = flagState === FlagStates.Flagged;

  const columns = useMemo(
    () =>
      buildReconFlagColumns(t, theme, {
        enableBulkSelection: bulkSelectionEnabled,
      }),
    [t, theme, bulkSelectionEnabled],
  );
  const pageCount = Math.max(1, Math.ceil(filteredGridTotalCount / pageSize));

  // After dismiss/resolve shrinks the result set, an empty trailing page looks like
  // "no flags left" even when earlier pages still have issues — snap back to page 1.
  useEffect(() => {
    if (loading || suppressGridResults) {
      return;
    }
    if (page > pageCount) {
      setPage(1);
    }
  }, [loading, suppressGridResults, page, pageCount, setPage]);

  const { table, rowSelection, setRowSelection } = useDataTable({
    data: serverFlags,
    columns,
    getRowId: (row) => row.id,
    enableRowSelection: bulkSelectionEnabled,
    paginationMode: 'manual',
    pageCount,
    pagination: {
      pageIndex: Math.max(0, page - 1),
      pageSize,
    },
    onPaginationChange: ((updater) => {
      const current: PaginationState = {
        pageIndex: Math.max(0, page - 1),
        pageSize,
      };
      const next = typeof updater === 'function' ? updater(current) : updater;
      if (next.pageSize !== pageSize) {
        setPageSize(next.pageSize as RowsPerPageOption);
        setPage(1);
      } else if (next.pageIndex !== current.pageIndex) {
        setPage(next.pageIndex + 1);
      }
    }) as OnChangeFn<PaginationState>,
    rowsPerPage: pageSize,
    tableOptions: {
      defaultColumn: {
        enableSorting: false,
      },
    },
  });

  const selectedCount = useMemo(
    () => Object.keys(rowSelection).filter((id) => rowSelection[id]).length,
    [rowSelection],
  );

  useEffect(() => {
    if (!bulkSelectionEnabled) {
      setRowSelection({});
    }
  }, [bulkSelectionEnabled, setRowSelection]);

  useEffect(() => {
    const valid = new Set(serverFlags.map((f) => f.id));
    setRowSelection((prev: RowSelectionState) => {
      const next: RowSelectionState = {};
      for (const id of Object.keys(prev)) {
        if (prev[id] && valid.has(id)) {
          next[id] = true;
        }
      }
      return next;
    });
  }, [serverFlags, setRowSelection]);

  const previousPageRef = useRef(page);
  useEffect(() => {
    if (previousPageRef.current === page) return;
    previousPageRef.current = page;
    setRowSelection({});
  }, [page, setRowSelection]);

  const clearSelection = useCallback(() => {
    setRowSelection({});
  }, [setRowSelection]);

  const selectAllVisible = useCallback(() => {
    table.toggleAllRowsSelected(true);
  }, [table]);

  const [bulkTransitionFlagsForGrid, bulkOptions] = useMutation(
    BULK_TRANSITION_FLAGS_FOR_GRID,
  );

  const dismissSelected = useCallback(async (): Promise<boolean> => {
    if (!bulkSelectionEnabled) {
      return false;
    }
    const selectedRows = table.getSelectedRowModel().rows;
    const ids = selectedRows
      .filter((r) => r.original.state === FlagStates.Flagged)
      .map((r) => r.original.id);
    if (ids.length === 0) {
      if (selectedRows.length > 0) {
        enqueueSnackbar(t('reconFlags.bulkDismissNoFlaggedSelected'), {
          variant: 'warning',
        });
      }
      return false;
    }
    try {
      const { data: mutationData, errors } = await bulkTransitionFlagsForGrid({
        variables: {
          input: { flagIds: ids, transition: FlagTransition.Trivial },
        },
      });
      if (errors?.length) {
        enqueueSnackbar(t('reconFlags.bulkDismissError'), { variant: 'error' });
        return false;
      }
      const failed = mutationData?.bulkTransitionFlagsForGrid?.failed ?? [];
      if (failed.length > 0) {
        enqueueSnackbar(
          t('reconFlags.bulkDismissPartial', {
            failed: failed.length,
            total: ids.length,
          }),
          { variant: 'warning' },
        );
        const failedIds = new Set(
          failed.map(
            (
              entry: BulkTransitionFlagsForGridMutation['bulkTransitionFlagsForGrid']['failed'][number],
            ) => entry.flagId,
          ),
        );
        setRowSelection((prev: RowSelectionState) => {
          const next: RowSelectionState = {};
          for (const id of Object.keys(prev)) {
            if (prev[id] && failedIds.has(id)) {
              next[id] = true;
            }
          }
          return next;
        });
        await refetchAfterFlagMutation();
        return false;
      }
      enqueueSnackbar(
        t('reconFlags.bulkDismissSuccess', { count: ids.length }),
        { variant: 'success' },
      );
      clearSelection();
      const dismissedIdSet = new Set(ids);
      const dismissedEntireVisiblePage =
        serverFlags.length > 0 &&
        serverFlags.every((row) => dismissedIdSet.has(row.id));
      if (dismissedEntireVisiblePage && page > 1) {
        setPage(1);
      }
      await refetchAfterFlagMutation();
      return true;
    } catch {
      enqueueSnackbar(t('reconFlags.bulkDismissError'), { variant: 'error' });
      return false;
    }
  }, [
    bulkSelectionEnabled,
    table,
    bulkTransitionFlagsForGrid,
    clearSelection,
    setRowSelection,
    refetchAfterFlagMutation,
    serverFlags,
    page,
    setPage,
    enqueueSnackbar,
    t,
  ]);

  const tableLoading =
    suppressGridResults ||
    (loading && !previousData && gridFilterKey === resolvedGridFilterKey);

  const gridProps = useMemo(
    () => ({
      theme,
      table,
      pageSize,
      tableLoading,
      pageCount,
      page,
      onPageChange: setPage,
      totalRowCount: filteredGridTotalCount,
      onPerPageChange: (nextPerPage: number) => {
        setPageSize(nextPerPage as RowsPerPageOption);
        setPage(1);
      },
      onRowClick,
      selectedCount,
      onSelectAllOnPage: selectAllVisible,
      onClearSelection: clearSelection,
      onBulkDismiss: dismissSelected,
      bulkDismissLoading: bulkOptions.loading,
      gridQueryLoading: suppressGridResults || loading,
    }),
    [
      theme,
      table,
      pageSize,
      tableLoading,
      pageCount,
      page,
      setPage,
      filteredGridTotalCount,
      setPageSize,
      onRowClick,
      selectedCount,
      selectAllVisible,
      clearSelection,
      dismissSelected,
      bulkOptions.loading,
      suppressGridResults,
      loading,
    ],
  );

  return { gridProps, refetch };
};
